/**
 * Модель раздела «Дубли»: форма ответов службы (`Duplicates.Contracts`, PR #1181) и то, что из них строит экран. Значения
 * строк (`state`, `step`, `phase`) — слова контракта дословно: клиент их не переименовывает и не добавляет своих.
 */

export const PAIR_STATES = ['undecided', 'marked', 'not-duplicate'] as const
export type PairState = (typeof PAIR_STATES)[number]

export const PAIR_FILTERS = ['all', 'undecided', 'marked', 'not-duplicate'] as const
export type PairFilter = (typeof PAIR_FILTERS)[number]

export const COPY_SIDES = ['first', 'second'] as const
export type CopySide = (typeof COPY_SIDES)[number]

export const MATCH_KINDS = ['lexical', 'semantic', 'both'] as const
export type MatchKind = (typeof MATCH_KINDS)[number]

export const SCAN_STATES = ['running', 'waiting', 'stopped', 'completed', 'failed'] as const
export type ScanState = (typeof SCAN_STATES)[number]

export const SCAN_STEPS = ['export', 'map', 'compare'] as const
export type ScanStep = (typeof SCAN_STEPS)[number]

export const STEP_STATES = ['pending', 'running', 'waiting', 'done', 'failed'] as const
export type StepState = (typeof STEP_STATES)[number]

export const STEP_PHASES = ['sent', 'awaiting-response', 'received', 'awaiting-search', 'awaiting-other-scan'] as const
export type StepPhase = (typeof STEP_PHASES)[number]

export const EXPORT_FORMATS = ['xlsx', 'json', 'txt', 'xml'] as const
export type ExportFormat = (typeof EXPORT_FORMATS)[number]

export const EXPORT_SCOPES = ['marked', 'all'] as const
export type ExportScope = (typeof EXPORT_SCOPES)[number]

/** Порог совпадения и размер страницы — числа договора (`DuplicateScanLimits`, `DuplicatePairLimits`). */
export const THRESHOLD_MIN = 80
export const THRESHOLD_MAX = 100
export const THRESHOLD_DEFAULT = 90
export const PAGE_SIZE = 50
/** Как часто спрашивается состояние, пока проверка идёт. */
export const SCAN_POLL_MS = 2000

export interface DuplicateItem {
  key: string
  name: string
  code: string | null
  sku: string | null
  barcode: string | null
  moreBarcodes: number
  group: string | null
  markedCopyInPairId: number | null
}

export interface DuplicatePair {
  id: number
  first: DuplicateItem
  second: DuplicateItem
  percent: number
  matchedBy: MatchKind
  sharedWords: readonly string[]
  state: PairState
  copySide: CopySide | null
  decidedBy: string | null
  decidedAt: string | null
  firstGone: boolean
  secondGone: boolean
}

export interface PairCounters {
  all: number
  undecided: number
  marked: number
  notDuplicate: number
}

export interface PairsPage {
  resourceId: string
  scanId: string | null
  filter: PairFilter
  page: number
  pageSize: number
  total: number
  counters: PairCounters
  items: readonly DuplicatePair[]
}

export interface DecisionResult {
  pair: DuplicatePair
  counters: PairCounters
}

export interface ScanStepProgress {
  step: ScanStep
  state: StepState
  phase: StepPhase | null
  done: number
  total: number | null
  sent: string | null
  awaiting: string | null
  received: string | null
  startedAt: string | null
  finishedAt: string | null
  retryAt: string | null
}

export interface ScanError {
  step: ScanStep
  code: string
  message: string
  requestId: string | null
  at: string
}

export interface Scan {
  id: string
  resourceId: string
  state: ScanState
  currentStep: ScanStep | null
  threshold: number
  steps: readonly ScanStepProgress[]
  itemCount: number
  pairCount: number
  markedCount: number
  error: ScanError | null
  startedAt: string
  finishedAt: string | null
  canStop: boolean
  canResume: boolean
  canRestart: boolean
}

export interface ScanStatus {
  resourceId: string
  scan: Scan | null
}

export interface ScanSummary {
  scanId: string
  state: ScanState
  threshold: number
  itemCount: number
  pairCount: number
  markedCount: number
  startedAt: string
  finishedAt: string | null
}

export interface DuplicateSource {
  id: string
  kind: string
  displayName: string
  lastScan: ScanSummary | null
}

export interface PairsArgs {
  resourceId: string
  filter: PairFilter
  page: number
}

export type DecisionKind = 'copy' | 'not-duplicate' | 'reset'

/** Команда над парой: `view` — какая страница списка на экране, её кэш правится сразу. */
export interface DecisionArgs {
  pairId: number
  kind: DecisionKind
  /** Ключ выбранного товара — только для «Это копия». */
  key: string | null
  operationId: string
  view: PairsArgs
}

export const FILTER_LABELS: Readonly<Record<PairFilter, string>> = {
  all: 'Все в списке',
  undecided: 'Не разобраны',
  marked: 'Отмечены',
  'not-duplicate': 'Не дубли',
}

export function counterOf(counters: PairCounters, filter: PairFilter): number {
  switch (filter) {
    case 'all':
      return counters.all
    case 'undecided':
      return counters.undecided
    case 'marked':
      return counters.marked
    case 'not-duplicate':
      return counters.notDuplicate
  }
}

/** Ошибка команды или запроса так, как её нужно показать и передать администратору (О-1). */
export interface ShownError {
  title: string
  message: string
  code: string
  requestId: string | null
  /** Весь текст для «Копировать». */
  report: string
}

export interface PairView {
  pair: DuplicatePair
  /** Ключ выбранной карточки этой пары; `null` — ничего не выбрано. */
  selectedKey: string | null
  busy: boolean
  error: ShownError | null
}

export type ExportPhase = 'idle' | 'preparing' | 'saved' | 'failed'

export interface ExportView {
  phase: ExportPhase
  fileName: string | null
  error: ShownError | null
}

export interface DuplicatesVm {
  resourceId: string
  filter: PairFilter
  page: number
  pages: number
  /** `loading` — страницы ещё нет; `failed` — запрос отказал (текст в `loadError`); `ready` — список на экране (возможно, пустой). */
  status: 'loading' | 'ready' | 'failed'
  loadError: ShownError | null
  /** Список на экране, а страница перечитывается (ход проверки, команда). */
  refreshing: boolean
  total: number
  counters: PairCounters
  /** Проверка по этому источнику уже была: есть что разбирать и выгружать. */
  hasScan: boolean
  pairs: readonly PairView[]
  exportView: ExportView
}

/** Проверка источника глазами экрана: ответ службы и ход команд над ней. */
export interface ScanVm {
  loading: boolean
  loadError: ShownError | null
  scan: Scan | null
  /** Идёт запуск, остановка или продолжение. */
  busy: boolean
  commandError: ShownError | null
}

export interface ScanActions {
  /** «Найти дубли» и «Искать заново»: проверка с порогом 80–100 %. */
  start: (threshold: number) => void
  stop: () => void
  resume: () => void
  reload: () => void
}

export interface DuplicatesActions {
  filterChanged: (filter: PairFilter) => void
  pageChanged: (page: number) => void
  cardSelected: (pairId: number, key: string) => void
  markCopy: (pair: DuplicatePair, key: string) => void
  notDuplicate: (pair: DuplicatePair) => void
  reset: (pair: DuplicatePair) => void
  reload: () => void
  exportFile: (format: ExportFormat, scope: ExportScope) => void
}
